import { useEffect, useState } from "react";
import { AddressData } from "../types/address";
import useIsMount from "./useIsMount";
import useDebounce from "./useDebounce";
import useZipcodeData from "./queries/useZipcodeData";
import useDebouncedMemo from "./useDebouncedMemo";
import { Control, Path, useFormContext } from "react-hook-form";

interface UseAddressFormProps<T extends {}> {
  initialAddress?: AddressData;
  onAddressChange?: (address: AddressData, isValid: boolean) => void;
  path?: Path<T>;
  disabled?: boolean;
  isLoading?: boolean;
}

export interface UseAddressForm<T extends {}> {
  address: AddressData;
  setAddress: (newAddress: AddressData) => void;
  zipcode: string;
  street: string;
  number: string;
  city: AddressData['city'];
  neighborhood: string;
  complement: string;
  setZipcode: (newZipcode: string) => void;
  setStreet: (newStreet: string) => void;
  setNumber: (newNumber: string) => void;
  setCity: (newCity: AddressData['city']) => void;
  setNeighborhood: (newNeighborhood: string) => void;
  setComplement: (newComplement: string) => void;
  zipcodeDataIsPending: boolean;
  zipcodeError: string;
  control?: Control<T>;
  setValue?: Control<T>;
  path?: Path<T>;
  isLoading?: boolean;
  onZipcodeChange: () => void;
}

const useAddressForm = <T extends {}>({
  initialAddress,
  onAddressChange,
  path: hookFormPath,
  disabled,
  isLoading,
}: UseAddressFormProps<T>): UseAddressForm<T> => {
  const formMethods = useFormContext();

  const isMount = useIsMount();

  const [isZipcodeSetProgrammatically, setIsZipcodeSetProgrammatically] = useState(false);

  const [zipcodeError, setZipcodeError] = useState('');

  const [debouncedZipcode, setZipcode, zipcode, setZipcodeForce] = useDebounce(initialAddress?.zipcode || '', 800);
  const [debouncedStreet, setStreet, street, setStreetForce] = useDebounce(initialAddress?.street || '');
  const [debouncedNumber, setNumber, number, setNumberForce] = useDebounce(initialAddress?.number || '');
  const [debouncedNeighborhood, setNeighborhood, neighborhood, setNeighborhoodForce] = useDebounce(initialAddress?.neighborhood || '');
  const [debouncedComplement, setComplement, complement, setComplementForce] = useDebounce(initialAddress?.complement || '');

  const [city, setCity] = useState<AddressData['city']>(initialAddress?.city);

  const { mutate: getZipcodeData, isPending: zipcodeDataIsPending } = useZipcodeData({
    onMutate: () => {
      setZipcodeError('');
    },
    onSuccess: (response) => {
      const data = response.data.data;

      if (zipcode && Object.values(data).every(value => !value)) {
        setZipcodeError('CEP não encontrado');
      }

      setStreet(data.street);
      setNeighborhood(data.neighborhood);
      setCity(data.city);

      if (formMethods && hookFormPath) {
        formMethods.setValue(`${hookFormPath}.street`, (data.street || ''));
        formMethods.setValue(`${hookFormPath}.neighborhood`, (data.neighborhood || ''));
        formMethods.setValue(`${hookFormPath}.city`, data.city);
      }
    }
  });

  useEffect(() => {
    if (debouncedZipcode && !isMount && !disabled && !isZipcodeSetProgrammatically) {
      getZipcodeData(debouncedZipcode);
    } else if (!debouncedZipcode) {
      setZipcodeError('');
    }
  }, [debouncedZipcode]);

  const address = useDebouncedMemo(() => {
    const newAddress = ({
      zipcode: debouncedZipcode,
      street: debouncedStreet,
      number: debouncedNumber,
      neighborhood: debouncedNeighborhood,
      complement: debouncedComplement,
      city,
      city_id: city?.id,
    });

    const hasZipcode = !!debouncedZipcode;
    const hasValidCity = !!city?.id;
    const isValid = !hasZipcode || hasValidCity;

    onAddressChange?.(newAddress, isValid);

    return newAddress;
  }, [
    debouncedZipcode,
    debouncedStreet,
    debouncedNumber,
    debouncedNeighborhood,
    debouncedComplement,
    city,
  ]);

  const setAddress = (newAddress: AddressData) => {
    setIsZipcodeSetProgrammatically(true);

    setZipcodeForce(newAddress?.zipcode || '');
    setStreetForce(newAddress?.street || '');
    setNumberForce(newAddress?.number || '');
    setNeighborhoodForce(newAddress?.neighborhood || '');
    setComplementForce(newAddress?.complement || '');
    setCity(newAddress?.city);

    if (formMethods) {
      formMethods.setValue(`${hookFormPath}.zipcode`, newAddress?.zipcode || '');
      formMethods.setValue(`${hookFormPath}.street`, newAddress?.street || '');
      formMethods.setValue(`${hookFormPath}.number`, newAddress?.number || '');
      formMethods.setValue(`${hookFormPath}.neighborhood`, newAddress?.neighborhood || '');
      formMethods.setValue(`${hookFormPath}.complement`, newAddress?.complement || '');
      formMethods.setValue(`${hookFormPath}.city`, newAddress?.city);
    }
  };

  const onZipcodeChange = () => {
    setIsZipcodeSetProgrammatically(false);
  }

  useEffect(() => {
    if (isLoading === false) {
      setAddress(initialAddress || {});
    }
  }, [isLoading]);

  return {
    address: address || initialAddress || {},
    setAddress,
    zipcode,
    street,
    number,
    city,
    neighborhood,
    complement,
    setZipcode,
    setStreet,
    setNumber,
    setCity,
    setNeighborhood,
    setComplement,
    zipcodeDataIsPending,
    zipcodeError,
    path: hookFormPath,
    isLoading,
    onZipcodeChange,
  };
};

export default useAddressForm;
